import type { User } from 'firebase/auth'

import { firebaseAuthSource } from '@/lib/getUserAuthHeaders'

import type { GetCheckoutSessionDetailsResponse } from '@/clients/payment-service'

import type { IntentPrice } from './intent/prices'
import { intentCheckoutToPurchase } from './intentPurchase'
import type { Purchase } from './purchaseSessionParams'
import { fetchSessionDetails, fetchSessionDetailsWithRetry, pollIntentCheckout } from './sessionDetailsCache'
import type { CheckoutCountry } from './types'

export type FetchPurchaseContext = {
  user: User
  countryCode: CheckoutCountry
  guestEmail: string | undefined
  intentPrices: IntentPrice[]
  /** Retry a failed session read, for a page that shows the answer; an intent poll retries on its own. */
  retrySession?: { retries: number; delayMs: number }
}

/**
 * A purchase in the session shape both checkouts report: an intent reference once it is paid (null when its quote
 * cannot name a purchase), a Stripe session as payment-service returns it.
 */
export const fetchPurchase = async (
  purchase: Purchase,
  { user, countryCode, guestEmail, intentPrices, retrySession }: FetchPurchaseContext,
): Promise<GetCheckoutSessionDetailsResponse | null> => {
  if (purchase.kind === 'intent') {
    const intent = await pollIntentCheckout(user, purchase.id)
    return intentCheckoutToPurchase(intent, user.email ?? guestEmail, intentPrices)
  }
  const auth = firebaseAuthSource(user)
  return retrySession
    ? fetchSessionDetailsWithRetry(auth, countryCode, purchase.id, { ...retrySession, guestEmail })
    : fetchSessionDetails(auth, countryCode, purchase.id, guestEmail)
}
